import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { buildAbility, buildEmptyPermissions } from '@/features/authorization/utils/ability'
import { PERMISSIONS } from '@/features/authorization/constants/permissions'
import type { AuthState, AuthResponse, UserData } from '@/types/auth.types'
import { UserRole } from '@/types/auth.types'
import type { ViewType } from '@/types/user'

// Role permission mappings that mirror backend ROLE_PERMISSIONS
const ROLE_PERMISSION_MAPPINGS = {
    hr_admin: [
        // Employee Management - ALL
        PERMISSIONS.EMPLOYEE.VIEW.code,
        PERMISSIONS.EMPLOYEE.VIEW_ALL.code,
        PERMISSIONS.EMPLOYEE.CREATE.code,
        PERMISSIONS.EMPLOYEE.EDIT.code,
        PERMISSIONS.EMPLOYEE.DELETE.code,
        PERMISSIONS.EMPLOYEE.EXPORT.code,
        PERMISSIONS.EMPLOYEE.VIEW_COMPENSATION.code,
        PERMISSIONS.EMPLOYEE.VIEW_PERSONAL.code,
        PERMISSIONS.EMPLOYEE.VIEW_PERFORMANCE.code,
        PERMISSIONS.EMPLOYEE.EDIT_COMPENSATION.code,
        PERMISSIONS.EMPLOYEE.MANAGE_LIFECYCLE.code,
        PERMISSIONS.EMPLOYEE.VIEW_REPORTS.code,
        PERMISSIONS.EMPLOYEE.MANAGE_DOCUMENTS.code,
        PERMISSIONS.EMPLOYEE.VIEW_DIRECTORY.code,

        // HRIS Import - ALL (Critical for HR Admin)
        PERMISSIONS.HRIS_IMPORT.UPLOAD.code,
        PERMISSIONS.HRIS_IMPORT.VIEW_JOBS.code,
        PERMISSIONS.HRIS_IMPORT.VIEW_MAPPINGS.code,
        PERMISSIONS.HRIS_IMPORT.EDIT_MAPPINGS.code,
        PERMISSIONS.HRIS_IMPORT.CONFIRM_MAPPINGS.code,
        PERMISSIONS.HRIS_IMPORT.VIEW_RESULTS.code,
        PERMISSIONS.HRIS_IMPORT.DOWNLOAD_ERRORS.code,
        PERMISSIONS.HRIS_IMPORT.RETRY_FAILED.code,
        PERMISSIONS.HRIS_IMPORT.VIEW_AUDIT_TRAIL.code,

        // Surveys - ALL
        PERMISSIONS.SURVEYS.VIEW.code,
        PERMISSIONS.SURVEYS.CREATE.code,
        PERMISSIONS.SURVEYS.EDIT.code,
        PERMISSIONS.SURVEYS.DELETE.code,
        PERMISSIONS.SURVEYS.ACTIVATE.code,
        PERMISSIONS.SURVEYS.VIEW_RESPONSES.code,
        PERMISSIONS.SURVEYS.ANALYZE.code,
        PERMISSIONS.SURVEYS.EXPORT.code,
        PERMISSIONS.SURVEYS.RESPOND.code,
        PERMISSIONS.SURVEYS.VIEW_OWN.code,
        PERMISSIONS.SURVEYS.EXIT_MANAGE.code,
        PERMISSIONS.SURVEYS.ONBOARDING_MANAGE.code,
        PERMISSIONS.SURVEYS.TEMPLATES_MANAGE.code,
        PERMISSIONS.SURVEYS.ANONYMOUS_PROTECT.code,

        // Analytics - ALL
        PERMISSIONS.ANALYTICS.VIEW_OVERVIEW.code,
        PERMISSIONS.ANALYTICS.VIEW_DEMOGRAPHICS.code,
        PERMISSIONS.ANALYTICS.VIEW_ENGAGEMENT.code,
        PERMISSIONS.ANALYTICS.VIEW_RETENTION.code,
        PERMISSIONS.ANALYTICS.VIEW_COMPENSATION.code,
        PERMISSIONS.ANALYTICS.VIEW_TEAM.code,
        PERMISSIONS.ANALYTICS.EXPORT.code,
        PERMISSIONS.ANALYTICS.VIEW_BENCHMARKS.code,
        PERMISSIONS.ANALYTICS.CREATE_REPORTS.code,
        PERMISSIONS.ANALYTICS.SCHEDULE_REPORTS.code,

        // Users - Most
        PERMISSIONS.USERS.VIEW.code,
        PERMISSIONS.USERS.SEARCH.code,
        PERMISSIONS.USERS.CREATE.code,
        PERMISSIONS.USERS.EDIT.code,
        PERMISSIONS.USERS.RESET_PASSWORD.code,
        PERMISSIONS.USERS.MANAGE_MFA.code,
        PERMISSIONS.USERS.MANAGE_SESSIONS.code,

        // Admin - Limited
        PERMISSIONS.ADMIN.VIEW_SETTINGS.code,
        PERMISSIONS.ADMIN.VIEW_AUDIT_LOGS.code,
        PERMISSIONS.ADMIN.EXPORT_AUDIT_LOGS.code,

        // HR Intake - Full access
        PERMISSIONS.HR_INTAKE.VIEW.code,
        PERMISSIONS.HR_INTAKE.CREATE.code,
        PERMISSIONS.HR_INTAKE.UPDATE.code,
        PERMISSIONS.HR_INTAKE.DELETE.code,

        // Integrations - Most
        PERMISSIONS.INTEGRATIONS.SLACK_MANAGE.code,
        PERMISSIONS.INTEGRATIONS.SLACK_SEND.code,
        PERMISSIONS.INTEGRATIONS.SLACK_SURVEYS.code,
        PERMISSIONS.INTEGRATIONS.TEAMS_MANAGE.code,
        PERMISSIONS.INTEGRATIONS.TEAMS_SEND.code,

        // Kudos
        PERMISSIONS.KUDOS.SEND.code,
        PERMISSIONS.KUDOS.VIEW.code,
        PERMISSIONS.KUDOS.MANAGE.code,
        PERMISSIONS.KUDOS.EXPORT.code,

        // Impersonation - Limited
        PERMISSIONS.IMPERSONATION.START.code,
        PERMISSIONS.IMPERSONATION.END.code,
        PERMISSIONS.IMPERSONATION.TARGET_EMPLOYEE.code,
        PERMISSIONS.IMPERSONATION.TARGET_MANAGER.code,
        PERMISSIONS.IMPERSONATION.SCOPE_READ_ONLY.code,
        PERMISSIONS.IMPERSONATION.SCOPE_WRITE_LIMITED.code,
    ],

    people_manager: [
        // Employee - Team only
        PERMISSIONS.EMPLOYEE.VIEW.code,
        PERMISSIONS.EMPLOYEE.VIEW_SELF.code,
        PERMISSIONS.EMPLOYEE.VIEW_TEAM.code,
        PERMISSIONS.EMPLOYEE.EDIT_SELF.code,
        PERMISSIONS.EMPLOYEE.EDIT_TEAM.code,
        PERMISSIONS.EMPLOYEE.VIEW_DIRECTORY.code,

        // Surveys - Respond and view team
        PERMISSIONS.SURVEYS.RESPOND.code,
        PERMISSIONS.SURVEYS.VIEW_OWN.code,
        PERMISSIONS.SURVEYS.VIEW_RESPONSES.code,

        // Analytics - Team only
        PERMISSIONS.ANALYTICS.VIEW_OVERVIEW.code,
        PERMISSIONS.ANALYTICS.VIEW_TEAM.code,

        // Kudos
        PERMISSIONS.KUDOS.SEND.code,
        PERMISSIONS.KUDOS.VIEW.code,
    ],

    employee: [
        // Employee - Self only
        PERMISSIONS.EMPLOYEE.VIEW_SELF.code,
        PERMISSIONS.EMPLOYEE.EDIT_SELF.code,
        PERMISSIONS.EMPLOYEE.VIEW_DIRECTORY.code,

        // Surveys - Participate
        PERMISSIONS.SURVEYS.RESPOND.code,
        PERMISSIONS.SURVEYS.VIEW_OWN.code,

        // Analytics - Public only
        PERMISSIONS.ANALYTICS.VIEW_OVERVIEW.code,

        // Kudos - Send/receive
        PERMISSIONS.KUDOS.SEND.code,
        PERMISSIONS.KUDOS.VIEW.code,
    ],
} as const

const initialState: AuthState = {
    user: null,
    organization: null,
    permissions: [],
    ability: buildEmptyPermissions(),
    isAuthenticated: false,
    isLoading: true, // Start with loading true to prevent premature redirects
    error: null,
    isMockAuth: false,
}

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setAuthData: (state, action: PayloadAction<AuthResponse>) => {
            const { user } = action.payload

            state.user = user
            state.organization = user.organization
                ? {
                      uuid: user.organization.id,
                      name: user.organization.name,
                  }
                : null
            state.permissions = user.role?.permissions || []
            state.ability = buildAbility(user.role?.permissions || [], user.role?.role === 'admin')
            state.isAuthenticated = true
            state.isLoading = false
            state.error = null
        },

        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },

        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
            state.isLoading = false
        },

        updateUserData: (state, action: PayloadAction<Partial<UserData>>) => {
            if (action.payload) {
                // If we're setting a full user object (on initial load)
                if (!state.user && action.payload.uuid) {
                    state.user = action.payload as UserData
                    state.isAuthenticated = true
                    state.organization = action.payload.organization
                        ? {
                              uuid: action.payload.organization.id,
                              name: action.payload.organization.name,
                          }
                        : null
                } else if (state.user) {
                    // Otherwise, merge with existing user
                    state.user = { ...state.user, ...action.payload }
                }

                // Update permissions if role changed
                if (action.payload.role && state.user) {
                    const updatedRole = { ...state.user.role, ...action.payload.role }
                    state.permissions = updatedRole.permissions || []
                    state.ability = buildAbility(updatedRole.permissions || [], updatedRole.role === 'admin')
                }
            }
        },

        clearAuth: (state) => {
            state.user = null
            state.organization = null
            state.permissions = []
            state.ability = buildEmptyPermissions()
            state.isAuthenticated = false
            state.isLoading = false
            state.error = null
            state.isMockAuth = false
        },

        rehydrationComplete: (state) => {
            // If we have a user after rehydration, rebuild the ability and set authenticated
            if (state.user) {
                const roleValue = state.user.role
                    ? typeof state.user.role === 'object'
                        ? state.user.role.role
                        : state.user.role
                    : null
                state.ability = buildAbility(state.permissions || [], roleValue === 'admin')
                state.isAuthenticated = true
                state.isLoading = false
            }
            // If no user, keep loading true - AuthInitializer will handle checking tokens
            // and setting loading to false after token validation
        },

        setMockAuthData: (state, action: PayloadAction<ViewType>) => {
            const userType = action.payload

            // Set mock auth flag
            state.isMockAuth = true

            // Create mock user data based on role with complete employee data
            const mockUserData: UserData = {
                uuid: 'demo-user-123',
                email:
                    userType === 'hr'
                        ? 'sarah.johnson@company.com'
                        : userType === 'manager'
                          ? 'john.smith@company.com'
                          : 'jane.doe@company.com',
                full_name: userType === 'hr' ? 'Sarah Johnson' : userType === 'manager' ? 'John Smith' : 'Jane Doe',
                timezone: 'America/New_York',
                is_active: true,
                is_verified: true,
                email_verified_at: new Date().toISOString(),
                organization: {
                    id: 'demo-org-123',
                    name: 'Demo Corporation',
                },
                role: {
                    role:
                        userType === 'hr'
                            ? UserRole.HR_MANAGER
                            : userType === 'manager'
                              ? UserRole.PEOPLE_MANAGER
                              : UserRole.EMPLOYEE,
                    display_name:
                        userType === 'hr' ? 'HR Manager' : userType === 'manager' ? 'People Manager' : 'Employee',
                    scope_department: userType === 'hr' ? 'Human Resources' : userType === 'manager' ? 'Engineering' : 'Product',
                    permissions: [
                        ...ROLE_PERMISSION_MAPPINGS[
                            userType === 'hr' ? 'hr_admin' : userType === 'manager' ? 'people_manager' : 'employee'
                        ],
                    ],
                },
                employee:
                    userType === 'manager' || userType === 'employee'
                        ? {
                              id: 'demo-emp-123',
                              department: userType === 'manager' ? 'Engineering' : 'Product',
                              job_title: userType === 'manager' ? 'Engineering Manager' : 'Product Designer',
                              is_people_manager: userType === 'manager',
                              reports_to_id: userType === 'employee' ? 'demo-manager-456' : null,
                          }
                        : null,
                profile: {
                    preferences: {
                        theme: 'light',
                        notifications_enabled: true,
                        language: 'en',
                    },
                    profile_image_url: null,
                    onboarding_completed: true,
                },
                last_login_at: new Date().toISOString(),
                created: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days ago
                updated: new Date().toISOString(),
            }

            // Update state with mock data
            state.user = mockUserData
            state.organization = mockUserData.organization
                ? {
                      uuid: mockUserData.organization.id,
                      name: mockUserData.organization.name,
                  }
                : null
            state.permissions = mockUserData.role?.permissions || []
            state.ability = buildAbility(mockUserData.role?.permissions || [], mockUserData.role?.role === 'admin')
            state.isAuthenticated = true
            state.isLoading = false
            state.error = null
        },
    },
})

export const { setAuthData, setLoading, setError, updateUserData, clearAuth, rehydrationComplete, setMockAuthData } =
    authSlice.actions

export const authReducer = authSlice.reducer
export { authSlice }
